import React, { useEffect, useMemo, useState } from 'react';
import { FileDown, FileSpreadsheet, Loader2, Scale } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { pricingAPI } from '@/lib/api';
import PrintButton from '@/components/PrintButton';

const eur = v => v.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

export default function ComparisonPanel() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checked, setChecked] = useState(new Set());
    const [cols, setCols] = useState(new Set());
    const [exporting, setExporting] = useState(false);
    const [exportingXlsx, setExportingXlsx] = useState(false);

    useEffect(() => {
        pricingAPI.comparison()
            .then(r => {
                setData(r.data);
                setChecked(new Set(r.data.rows.map(x => x.recipe_id)));
                setCols(new Set(r.data.clients.map(c => c.id)));
            })
            .catch(() => toast.error('Erreur de chargement du comparatif'))
            .finally(() => setLoading(false));
    }, []);

    const allChecked = data ? checked.size === data.rows.length : false;
    const toggleAll = () => setChecked(allChecked ? new Set() : new Set(data.rows.map(x => x.recipe_id)));
    const toggleRow = (id) => setChecked(prev => {
        const s = new Set(prev);
        s.has(id) ? s.delete(id) : s.add(id);
        return s;
    });
    const toggleCol = (id) => setCols(prev => {
        const s = new Set(prev);
        s.has(id) ? s.delete(id) : s.add(id);
        return s;
    });

    const grouped = useMemo(() => {
        if (!data) return [];
        const out = [];
        let cat = null;
        for (const r of data.rows) {
            if (r.category !== cat) {
                out.push({ type: 'cat', category: r.category });
                cat = r.category;
            }
            out.push({ type: 'row', ...r });
        }
        return out;
    }, [data]);

    const exportPdf = async () => {
        setExporting(true);
        try {
            const resp = await pricingAPI.comparisonPdf([...checked], [...cols]);
            window.open(URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })), '_blank');
        } catch {
            toast.error('Erreur lors de la génération du PDF');
        } finally {
            setExporting(false);
        }
    };

    const exportXlsx = async () => {
        setExportingXlsx(true);
        try {
            const resp = await pricingAPI.comparisonXlsx([...checked], [...cols]);
            const a = document.createElement('a');
            a.href = URL.createObjectURL(new Blob([resp.data]));
            a.download = `grille_tarifaire_comparative_${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch {
            toast.error('Erreur lors de la génération du fichier Excel');
        } finally {
            setExportingXlsx(false);
        }
    };

    if (loading) return <div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>;
    if (!data?.rows?.length) return <div className="py-10 text-center text-slate-400 italic">Aucun produit avec tarif.</div>;

    const commissionNote = (c) => c.sale_type === 'carrefour'
        ? 'net −15% / TVA 2,1%'
        : c.commission ? `net −${Math.round(c.commission * 100)}%` : 'HT direct';

    return (
        <div className="space-y-3" data-testid="comparison-panel">
            <div className="flex flex-wrap items-center gap-3">
                <h2 className="font-bold text-slate-900 flex items-center gap-2 text-sm">
                    <Scale className="w-4 h-4 text-violet-600" />
                    Comparatif clients — tarifs nets HT
                </h2>
                <span className="text-xs text-slate-500" data-testid="comparison-selected-count">
                    {checked.size} / {data.rows.length} produits sélectionnés
                </span>
                <div className="ml-auto flex gap-2">
                    <Button size="sm" variant="outline" onClick={exportXlsx} disabled={!checked.size || !cols.size || exportingXlsx}
                        className="h-8 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        data-testid="comparison-export-xlsx-btn">
                        {exportingXlsx ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />}
                        Exporter Excel
                    </Button>
                    <Button size="sm" onClick={exportPdf} disabled={!checked.size || !cols.size || exporting}
                        className="h-8 text-xs bg-slate-900 text-white hover:bg-slate-800"
                        data-testid="comparison-export-pdf-btn">
                        {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1.5" />}
                        Exporter la grille PDF
                    </Button>
                    <PrintButton className="h-8" iconClass="w-3.5 h-3.5" testId="comparison-print-btn"
                        disabled={!checked.size || !cols.size}
                        getBlob={async () => (await pricingAPI.comparisonPdf([...checked], [...cols])).data} />
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-3 py-2 bg-slate-50 border rounded-lg text-xs"
                data-testid="comparison-columns-bar">
                <span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Colonnes export :</span>
                <label className="flex items-center gap-1.5 cursor-pointer text-slate-700">
                    <Checkbox checked={cols.has('cost')} onCheckedChange={() => toggleCol('cost')}
                        data-testid="comparison-col-cost" />
                    Revient/produit
                </label>
                {data.clients.map(c => (
                    <label key={c.id} className="flex items-center gap-1.5 cursor-pointer text-slate-700">
                        <Checkbox checked={cols.has(c.id)} onCheckedChange={() => toggleCol(c.id)}
                            data-testid={`comparison-col-${c.id}`} />
                        {c.name}
                    </label>
                ))}
            </div>
            <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b text-xs uppercase text-slate-500">
                        <tr>
                            <th className="px-3 py-2 w-8">
                                <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="comparison-check-all" />
                            </th>
                            <th className="text-left px-3 py-2">Produit</th>
                            <th className="text-right px-3 py-2">
                                Revient
                                <div className="text-[9px] normal-case font-normal text-slate-400">coût matière</div>
                            </th>
                            {data.clients.map(c => (
                                <th key={c.id} className="text-right px-3 py-2">
                                    {c.name}
                                    <div className="text-[9px] normal-case font-normal text-slate-400">{commissionNote(c)}</div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {grouped.map((item, i) => item.type === 'cat' ? (
                            <tr key={`cat-${i}`} className="bg-slate-100/70">
                                <td colSpan={3 + data.clients.length} className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                    {item.category}
                                </td>
                            </tr>
                        ) : (
                            <tr key={item.recipe_id}
                                className={`border-b border-slate-100 hover:bg-slate-50/60 ${checked.has(item.recipe_id) ? '' : 'opacity-45'}`}
                                data-testid={`comparison-row-${item.recipe_id}`}>
                                <td className="px-3 py-1.5">
                                    <Checkbox checked={checked.has(item.recipe_id)}
                                        onCheckedChange={() => toggleRow(item.recipe_id)}
                                        data-testid={`comparison-check-${item.recipe_id}`} />
                                </td>
                                <td className="px-3 py-1.5 font-medium text-slate-700">{item.name}</td>
                                <td className="px-3 py-1.5 text-right tabular-nums text-amber-700" data-testid={`comparison-cost-${item.recipe_id}`}>
                                    {item.cost > 0 ? eur(item.cost) : <span className="text-slate-300">—</span>}
                                </td>
                                {data.clients.map(c => (
                                    <td key={c.id} className="px-3 py-1.5 text-right tabular-nums">
                                        {item.prices[c.id] !== undefined
                                            ? <span className="font-semibold text-slate-800">{eur(item.prices[c.id])}</span>
                                            : <span className="text-slate-300">—</span>}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="text-[11px] text-slate-400 italic">
                Tarifs nets HT après commission (Carrefour : −15 % et TVA 2,1 % ; Socoarmes : −20 %). Revient = coût matière unitaire. Seuls les produits cochés dans la « Sélection » du client et ayant un tarif sont listés. Les colonnes cochées ci-dessus déterminent le contenu des exports PDF et Excel (revient exclu par défaut).
            </div>
        </div>
    );
}
